///
/// EarthBrowser - Memory Data Provider
///
/// In-memory data provider for GeoJSON, CSV, Shapefiles
/// Created by Matt Giger on December 17, 2025
/// Copyright (c) 2025 Geodata Labs
///

import type {
	IDataProvider,
	DataRow,
	DataQuery,
	QueryResult,
	ColumnMetadata,
	DataStats,
	ProviderCapabilities,
	FilterGroup,
	FilterCondition,
	DataChangeEvent,
	DataChangeType,
} from './types'

export interface MemoryDataProviderConfig {
	id: string
	name: string
	data: DataRow[]
	columns?: ColumnMetadata[]
}

/**
 * In-memory data provider for client-side data (GeoJSON, CSV, Shapefiles)
 *
 * Features:
 * - Fast filtering and sorting
 * - Full-text search
 * - Regex support
 * - CRUD operations
 * - Change notifications
 */
export class MemoryDataProvider implements IDataProvider {
	readonly id: string
	readonly name: string
	readonly type = 'memory' as const

	private data: DataRow[]
	private columnMetadata: ColumnMetadata[]
	private changeListeners = new Set<(event: DataChangeEvent) => void>()

	readonly capabilities: ProviderCapabilities = {
		supportsFiltering: true,
		supportsSorting: true,
		supportsPagination: true,
		supportsFullTextSearch: true,
		supportsRegex: true,
		supportsGeometry: true,
		supportsInsert: true,
		supportsUpdate: true,
		supportsDelete: true,
		supportsTransactions: false,
		supportsIndexing: false,
		supportsAggregation: false,
		supportsJoins: false,
		isRemote: false,
		supportsStreaming: false,
		supportsCaching: false,
	}

	constructor(config: MemoryDataProviderConfig) {
		this.id = config.id
		this.name = config.name
		this.data = [...config.data] // Copy to prevent external mutations
		this.columnMetadata = config.columns || this.inferColumns(this.data)
	}

	async getColumns(): Promise<ColumnMetadata[]> {
		return this.columnMetadata
	}

	async getStats(): Promise<DataStats> {
		return {
			totalRows: this.data.length,
			columnCount: this.columnMetadata.length,
			memorySize: this.estimateMemorySize(),
			lastModified: new Date(),
		}
	}

	async query(query: DataQuery): Promise<QueryResult> {
		const startTime = performance.now()

		let results = [...this.data]

		// Apply filters
		if (query.filters) {
			results = results.filter(row => this.matchesFilter(row, query.filters!))
		}

		// Apply full-text search
		if (query.search) {
			results = results.filter(row => this.matchesSearch(row, query.search))
		}

		// Get total count before pagination
		const totalCount = results.length

		// Apply sorting
		if (query.sort && query.sort.length > 0) {
			results = this.sortResults(results, query.sort)
		}

		// Apply pagination
		const offset = query.offset || 0
		const limit = query.limit || totalCount
		const paginatedResults = results.slice(offset, offset + limit)

		// Apply column selection
		let finalResults = paginatedResults
		if (query.columns && query.columns.length > 0) {
			finalResults = paginatedResults.map(row =>
				this.selectColumns(row, query.columns!)
			)
		}

		const executionTime = performance.now() - startTime

		return {
			rows: finalResults,
			totalCount,
			offset,
			limit,
			hasMore: offset + limit < totalCount,
			executionTime,
			fromCache: false,
		}
	}

	async count(filters?: FilterGroup): Promise<number> {
		if (!filters) {
			return this.data.length
		}

		return this.data.filter(row => this.matchesFilter(row, filters)).length
	}

	async insert(rows: DataRow[]): Promise<void> {
		this.data.push(...rows)
		this.emitChange('insert', rows.length)
	}

	async update(filters: FilterGroup, updates: Partial<DataRow>): Promise<number> {
		let affectedRows = 0

		// Filter out undefined values from updates
		const cleanUpdates: DataRow = {}
		for (const [key, value] of Object.entries(updates)) {
			if (value !== undefined) {
				cleanUpdates[key] = value
			}
		}

		this.data = this.data.map(row => {
			if (this.matchesFilter(row, filters)) {
				affectedRows++
				return { ...row, ...cleanUpdates }
			}
			return row
		})

		if (affectedRows > 0) {
			this.emitChange('update', affectedRows)
		}

		return affectedRows
	}

	async delete(filters: FilterGroup): Promise<number> {
		const originalLength = this.data.length
		this.data = this.data.filter(row => !this.matchesFilter(row, filters))
		const affectedRows = originalLength - this.data.length

		if (affectedRows > 0) {
			this.emitChange('delete', affectedRows)
		}

		return affectedRows
	}

	onDataChange(callback: (event: DataChangeEvent) => void): () => void {
		this.changeListeners.add(callback)
		return () => this.changeListeners.delete(callback)
	}

	// Private helper methods

	private inferColumns(data: DataRow[]): ColumnMetadata[] {
		if (data.length === 0) return []

		const firstRow = data[0]
		return Object.keys(firstRow).map(key => ({
			id: key,
			name: key,
			type: this.inferType(firstRow[key]),
			nullable: true,
		}))
	}

	private inferType(value: unknown): ColumnMetadata['type'] {
		if (value === null || value === undefined) return 'unknown'
		if (typeof value === 'string') return 'string'
		if (typeof value === 'number') return 'number'
		if (typeof value === 'boolean') return 'boolean'
		if (value instanceof Date) return 'date'
		if (typeof value === 'object' && value !== null) {
			// Check if it's a GeoJSON geometry
			const obj = value as Record<string, unknown>
			if (obj.type && obj.coordinates) return 'geometry'
			return 'json'
		}
		return 'unknown'
	}

	private matchesFilter(row: DataRow, filter: FilterGroup): boolean {
		const results = filter.conditions.map(condition => {
			if ('operator' in condition && condition.operator === 'AND' || condition.operator === 'OR') {
				// Nested filter group
				return this.matchesFilter(row, condition)
			} else {
				// Filter condition
				return this.matchesCondition(row, condition as FilterCondition)
			}
		})

		return filter.operator === 'AND'
			? results.every(r => r)
			: results.some(r => r)
	}

	private matchesCondition(row: DataRow, condition: FilterCondition): boolean {
		const value = row[condition.column]

		switch (condition.operator) {
			case 'equals':
				return this.compareValues(value, condition.value, condition.caseSensitive) === 0

			case 'not_equals':
				return this.compareValues(value, condition.value, condition.caseSensitive) !== 0

			case 'contains':
				if (typeof value === 'string' && typeof condition.value === 'string') {
					const str = condition.caseSensitive ? value : value.toLowerCase()
					const search = condition.caseSensitive ? condition.value : condition.value.toLowerCase()
					return str.includes(search)
				}
				return false

			case 'not_contains':
				if (typeof value === 'string' && typeof condition.value === 'string') {
					const str = condition.caseSensitive ? value : value.toLowerCase()
					const search = condition.caseSensitive ? condition.value : condition.value.toLowerCase()
					return !str.includes(search)
				}
				return true

			case 'starts_with':
				if (typeof value === 'string' && typeof condition.value === 'string') {
					const str = condition.caseSensitive ? value : value.toLowerCase()
					const search = condition.caseSensitive ? condition.value : condition.value.toLowerCase()
					return str.startsWith(search)
				}
				return false

			case 'ends_with':
				if (typeof value === 'string' && typeof condition.value === 'string') {
					const str = condition.caseSensitive ? value : value.toLowerCase()
					const search = condition.caseSensitive ? condition.value : condition.value.toLowerCase()
					return str.endsWith(search)
				}
				return false

			case 'greater_than':
				return this.compareValues(value, condition.value) > 0

			case 'greater_than_or_equal':
				return this.compareValues(value, condition.value) >= 0

			case 'less_than':
				return this.compareValues(value, condition.value) < 0

			case 'less_than_or_equal':
				return this.compareValues(value, condition.value) <= 0

			case 'in':
				if (Array.isArray(condition.value)) {
					return condition.value.some(v => this.compareValues(value, v, condition.caseSensitive) === 0)
				}
				return false

			case 'not_in':
				if (Array.isArray(condition.value)) {
					return !condition.value.some(v => this.compareValues(value, v, condition.caseSensitive) === 0)
				}
				return true

			case 'is_null':
				return value === null || value === undefined

			case 'is_not_null':
				return value !== null && value !== undefined

			case 'between':
				if (Array.isArray(condition.value) && condition.value.length === 2) {
					return this.compareValues(value, condition.value[0]) >= 0 &&
						this.compareValues(value, condition.value[1]) <= 0
				}
				return false

			case 'regex':
				if (typeof value === 'string' && typeof condition.value === 'string') {
					const flags = condition.caseSensitive ? '' : 'i'
					const regex = new RegExp(condition.value, flags)
					return regex.test(value)
				}
				return false

			default:
				return false
		}
	}

	private compareValues(a: unknown, b: unknown, caseSensitive = true): number {
		// Handle null/undefined
		if (a === null || a === undefined) return b === null || b === undefined ? 0 : -1
		if (b === null || b === undefined) return 1

		// String comparison
		if (typeof a === 'string' && typeof b === 'string') {
			const strA = caseSensitive ? a : a.toLowerCase()
			const strB = caseSensitive ? b : b.toLowerCase()
			return strA < strB ? -1 : strA > strB ? 1 : 0
		}

		// Number comparison
		if (typeof a === 'number' && typeof b === 'number') {
			return a - b
		}

		// Date comparison
		if (a instanceof Date && b instanceof Date) {
			return a.getTime() - b.getTime()
		}

		// Boolean comparison
		if (typeof a === 'boolean' && typeof b === 'boolean') {
			return a === b ? 0 : a ? 1 : -1
		}

		// Default: convert to string and compare
		return this.stringifyValue(a).localeCompare(this.stringifyValue(b))
	}

	// Explicitly convert an arbitrary value to a string for comparison/search
	private stringifyValue(value: unknown): string {
		if (typeof value === 'string') return value
		if (value === null || value === undefined) return String(value)
		if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') return String(value)
		if (value instanceof Date) return value.toString()
		if (Array.isArray(value)) return value.map(v => this.stringifyValue(v)).join(',')
		return JSON.stringify(value)
	}

	private matchesSearch(row: DataRow, search: DataQuery['search']): boolean {
		if (!search) return true

		const searchLower = search.query.toLowerCase()
		const columnsToSearch = search.columns || Object.keys(row)

		return columnsToSearch.some(column => {
			const value = row[column]
			if (value === null || value === undefined) return false

			const strValue = this.stringifyValue(value).toLowerCase()
			return strValue.includes(searchLower)
		})
	}

	private sortResults(results: DataRow[], sort: NonNullable<DataQuery['sort']>): DataRow[] {
		return [...results].sort((a, b) => {
			for (const spec of sort) {
				const comparison = this.compareValues(a[spec.column], b[spec.column])
				if (comparison !== 0) {
					return spec.direction === 'asc' ? comparison : -comparison
				}
			}
			return 0
		})
	}

	private selectColumns(row: DataRow, columns: string[]): DataRow {
		const result: DataRow = {}
		for (const column of columns) {
			if (column in row) {
				result[column] = row[column]
			}
		}
		return result
	}

	private estimateMemorySize(): number {
		// Rough estimate: JSON stringify size
		try {
			return new Blob([JSON.stringify(this.data)]).size
		} catch {
			return 0
		}
	}

	private emitChange(type: DataChangeType, affectedRows?: number): void {
		const event: DataChangeEvent = {
			type,
			affectedRows,
			timestamp: new Date(),
		}

		this.changeListeners.forEach(listener => listener(event))
	}
}

